import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { snakeToCamel, useCurrentUserDateTimeFormat, useCurrentUserTimezone } from '@ingenico/react-section-utils';
import { CoreStatus } from '@ingenico/react-ui/core-status';
import { CoreTypography, CoreTypographyVariant } from '@ingenico/react-ui/core-typography';
import { useRenderDefaultColumHeader } from '@ingenico/react-ui/datagrid';
import { DateTime } from '@ingenico/react-ui/date-time';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { toString } from 'lodash-es';
import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { DeploymentUnitResourceItemV2, EnumDeploymentUnitStatus } from '../../domain/models/deployment-unit.model';
import styles from './DeploymentTargetDevicesTab.module.scss';
import { getDeploymentTargetDevicesChipStatusColor } from './DeploymentTargetDevicesTab.util';

export function useDeploymentTargetDevicesTabColumns(): {
  defaultAvailableColumns: string[];
  columns: GridColDef[];
} {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.DEPLOYMENT, ALL_LNGS_NAMESPACES.COMMON]);
  const timezone = useCurrentUserTimezone();
  const currentUserDateTimeFormat = useCurrentUserDateTimeFormat();
  const renderCellValue = useCallback(
    (params: GridRenderCellParams<DeploymentUnitResourceItemV2, DeploymentUnitResourceItemV2>): ReactNode =>
      params?.value ? (
        <CoreTypography
          variant={CoreTypographyVariant.Body02Regular}
          customClassName={styles.targetDevicesTab__primaryText}
          tooltipTitle={toString(params?.value)}
          truncateText
          showTooltip
        >
          {toString(params?.value)}
        </CoreTypography>
      ) : (
        t('notAvailable')
      ),
    [t],
  );

  const renderTwoLineCell = useCallback((primaryText: string, secondaryText: string) => {
    return (
      <div>
        <CoreTypography
          variant={CoreTypographyVariant.Body02Medium}
          customClassName={styles.targetDevicesTab__primaryText}
        >
          {primaryText}
        </CoreTypography>
        <CoreTypography
          variant={CoreTypographyVariant.Body02Regular}
          customClassName={styles.targetDevicesTab__secondaryText}
        >
          {secondaryText}
        </CoreTypography>
      </div>
    );
  }, []);

  const renderModelAndManufacturer = useCallback(
    (params: GridRenderCellParams<DeploymentUnitResourceItemV2, DeploymentUnitResourceItemV2>) => {
      const deploymentUnit = params.row;
      return renderTwoLineCell(
        toString(deploymentUnit.physicalDevice.model),
        toString(deploymentUnit.physicalDevice.manufacturer),
      );
    },
    [renderTwoLineCell],
  );

  const renderCellSerialNumberAndPartNumber = useCallback(
    (params: GridRenderCellParams<DeploymentUnitResourceItemV2, DeploymentUnitResourceItemV2>) => {
      const deploymentUnit = params.row;
      return renderTwoLineCell(
        toString(deploymentUnit.physicalDevice.serialNumber),
        toString(deploymentUnit.physicalDevice.partNumber),
      );
    },
    [renderTwoLineCell],
  );

  const renderCellStatus = useCallback(
    (params: GridRenderCellParams<DeploymentUnitResourceItemV2, EnumDeploymentUnitStatus>) => {
      const statusType = getDeploymentTargetDevicesChipStatusColor(params.value);
      const statusLabel = snakeToCamel(toString(params.value));
      return params.value ? (
        <CoreStatus
          transparentBackground
          label={t(`status.${statusLabel}`, { ns: ALL_LNGS_NAMESPACES.DEPLOYMENT })}
          status={statusType}
          customColorsClassNames={{ label: styles.labelTextColor }}
        />
      ) : (
        t('notAvailable')
      );
    },
    [t],
  );

  const renderCellDate = useCallback(
    (params: GridRenderCellParams<DeploymentUnitResourceItemV2>) =>
      params.value ? (
        <DateTime
          instant={new Date(params.value).toISOString()}
          userDateTimeFormat={currentUserDateTimeFormat}
          timezone={timezone}
        />
      ) : (
        ''
      ),
    [currentUserDateTimeFormat, timezone],
  );

  const { defaultRenderColumnHeader } = useRenderDefaultColumHeader(t);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        renderHeader: defaultRenderColumnHeader('field.targetDevices.name', ALL_LNGS_NAMESPACES.DEPLOYMENT),
        headerName: t('field.targetDevices.name', { ns: ALL_LNGS_NAMESPACES.DEPLOYMENT }),
        filterable: false,
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: renderCellValue,
        valueGetter: (_, row: DeploymentUnitResourceItemV2) => row.logicalDevice?.name,
      },
      {
        field: 'logicalDevice.reference',
        renderHeader: defaultRenderColumnHeader('field.targetDevices.virtualReference', ALL_LNGS_NAMESPACES.DEPLOYMENT),
        headerName: t('field.targetDevices.virtualReference', { ns: ALL_LNGS_NAMESPACES.DEPLOYMENT }),
        filterable: false,
        flex: 1.3,
        minWidth: 150,
        sortable: false,
        renderCell: renderCellValue,
        valueGetter: (_, row: DeploymentUnitResourceItemV2) => row.logicalDevice?.reference,
      },
      {
        field: 'model',
        renderHeader: defaultRenderColumnHeader(
          'field.targetDevices.modelAndManufacturer',
          ALL_LNGS_NAMESPACES.DEPLOYMENT,
        ),
        headerName: t('field.targetDevices.modelAndManufacturer', { ns: ALL_LNGS_NAMESPACES.DEPLOYMENT }),
        filterable: false,
        flex: 1.3,
        minWidth: 150,
        sortable: false,
        renderCell: renderModelAndManufacturer,
        valueGetter: (_, row: DeploymentUnitResourceItemV2) =>
          `${row.physicalDevice?.model} ${row.physicalDevice?.manufacturer}`,
      },
      {
        field: 'serialNumber',
        renderHeader: defaultRenderColumnHeader('field.targetDevices.serialNumber', ALL_LNGS_NAMESPACES.DEPLOYMENT),
        headerName: t('field.serialNumber', { ns: ALL_LNGS_NAMESPACES.DEPLOYMENT }),
        filterable: false,
        flex: 1.3,
        minWidth: 150,
        sortable: false,
        renderCell: renderCellSerialNumberAndPartNumber,
        valueGetter: (_, row: DeploymentUnitResourceItemV2) =>
          `${row.physicalDevice?.serialNumber} ${row.physicalDevice?.partNumber}`,
      },
      {
        field: 'merchantName',
        renderHeader: defaultRenderColumnHeader('field.targetDevices.merchant', ALL_LNGS_NAMESPACES.DEPLOYMENT),
        headerName: t('field.targetDevices.merchant', { ns: ALL_LNGS_NAMESPACES.DEPLOYMENT }),
        filterable: false,
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: renderCellValue,
      },
      {
        field: 'startDate',
        renderHeader: defaultRenderColumnHeader('field.targetDevices.startDate', ALL_LNGS_NAMESPACES.DEPLOYMENT),
        headerName: t('field.targetDevices.startDate', { ns: ALL_LNGS_NAMESPACES.DEPLOYMENT }),
        filterable: false,
        flex: 1.3,
        minWidth: 150,
        renderCell: renderCellDate,
      },
      {
        field: 'endDate',
        renderHeader: defaultRenderColumnHeader('field.targetDevices.endDate', ALL_LNGS_NAMESPACES.DEPLOYMENT),
        headerName: t('field.targetDevices.endDate', { ns: ALL_LNGS_NAMESPACES.DEPLOYMENT }),
        filterable: false,
        flex: 1.3,
        minWidth: 150,
        renderCell: renderCellDate,
      },
      {
        field: 'status',
        renderHeader: defaultRenderColumnHeader('field.targetDevices.status', ALL_LNGS_NAMESPACES.DEPLOYMENT),
        headerName: t('field.targetDevices.status', { ns: ALL_LNGS_NAMESPACES.DEPLOYMENT }),
        filterable: false,
        flex: 1,
        minWidth: 150,
        renderCell: renderCellStatus,
      },
    ],
    [
      defaultRenderColumnHeader,
      t,
      renderCellValue,
      renderModelAndManufacturer,
      renderCellSerialNumberAndPartNumber,
      renderCellDate,
      renderCellStatus,
    ],
  );

  const defaultAvailableColumns = columns.map((column) => column.field);

  return {
    columns,
    defaultAvailableColumns,
  };
}
